import { useState, useRef } from "react";
import { Upload, Download, Mail, Phone, Eye, Send, AlertTriangle, CheckCircle, XCircle, FileText } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Progress } from "./ui/progress";

interface MailRecipient {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  message: string;
  isValid: boolean;
  errors: string[];
}

interface ValidationResult {
  valid: MailRecipient[];
  invalid: MailRecipient[];
  totalRows: number;
  validRows: number;
  invalidRows: number;
}

export function MailSender() {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [previewDialog, setPreviewDialog] = useState({
    isOpen: false,
    type: "email" as "email" | "sms"
  });
  const [sendComplete, setSendComplete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone: string): boolean => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, '');
    // Check if it's a valid international format (7-15 digits)
    return cleanPhone.length >= 7 && cleanPhone.length <= 15;
  };

  const validateName = (name: string): boolean => {
    return name.trim().length >= 2;
  };

  const validateMessage = (message: string): boolean => {
    return message.trim().length >= 1;
  };

  const parseCSV = (csvText: string): MailRecipient[] => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const recipients: MailRecipient[] = [];
    
    // Skip header row
    for (let i = 1; i < lines.length; i++) {
      const columns = lines[i].split(',').map(col => col.trim().replace(/"/g, ''));
      
      if (columns.length >= 5) {
        const [firstName, lastName, email, phoneNumber, message] = columns;
        const errors: string[] = [];
        
        // Validate each field
        if (!validateName(firstName)) {
          errors.push("First name must be at least 2 characters");
        }
        if (!validateName(lastName)) {
          errors.push("Last name must be at least 2 characters");
        }
        if (!validateEmail(email)) {
          errors.push("Invalid email format");
        }
        if (!validatePhoneNumber(phoneNumber)) {
          errors.push("Invalid phone number format (should be 7-15 digits with country code)");
        }
        if (!validateMessage(message)) {
          errors.push("Message cannot be empty");
        }
        
        recipients.push({
          firstName,
          lastName,
          email,
          phoneNumber,
          message,
          isValid: errors.length === 0,
          errors
        });
      }
    }
    
    return recipients;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      alert('Please upload a CSV or Excel file');
      return;
    }

    setUploadedFile(file);
    setIsProcessing(true);
    setValidationResult(null);

    try {
      const text = await file.text();
      const recipients = parseCSV(text);
      
      const valid = recipients.filter(r => r.isValid);
      const invalid = recipients.filter(r => !r.isValid);
      
      setValidationResult({
        valid,
        invalid,
        totalRows: recipients.length,
        validRows: valid.length,
        invalidRows: invalid.length
      });
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file. Please check the format.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.csv') || file.name.endsWith('.xlsx')) {
        // Simulate file input change
        const input = fileInputRef.current;
        if (input) {
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          input.files = dataTransfer.files;
          handleFileUpload({ target: { files: dataTransfer.files } } as any);
        }
      } else {
        alert('Please upload a CSV or Excel file');
      }
    }
  };

  const downloadTemplate = () => {
    const template = `FirstName,LastName,Email,PhoneNumber,Message
John,Doe,john.doe@example.com,+1234567890,Welcome to Coachable! Your journey to athletic excellence begins here.
Jane,Smith,jane.smith@example.com,+1987654321,Your Coachable subscription is ready. Let's start your training!
Mike,Johnson,mike.johnson@example.com,+1555123456,Thank you for joining Coachable - where passion meets preparation.`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'coachable_mail_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSend = async (type: "email" | "sms") => {
    if (!validationResult || validationResult.validRows === 0) return;
    
    setIsSending(true);
    setSendProgress(0);
    setSendComplete(false);

    try {
      // Simulate sending process
      for (let i = 0; i <= validationResult.validRows; i++) {
        await new Promise(resolve => setTimeout(resolve, 100));
        setSendProgress((i / validationResult.validRows) * 100);
      }

      console.log(`Sending ${type} to ${validationResult.validRows} recipients`);
      
      // Simulate API call
      // const response = await fetch(`host/api/v1/bulk-${type}`, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     Authorization: `Bearer ${localStorage.getItem("authToken")}`
      //   },
      //   body: JSON.stringify({
      //     recipients: validationResult.valid,
      //     type
      //   })
      // });

      setSendComplete(true);
    } catch (error) {
      console.error(`Error sending ${type}:`, error);
      alert(`Error sending ${type}. Please try again.`);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1>Mail Sender</h1>
          <p className="text-muted-foreground">
            Send bulk emails and SMS messages by uploading recipient data
          </p>
        </div>
        <Button variant="outline" onClick={downloadTemplate}>
          <Download className="h-4 w-4 mr-2" />
          Download Template
        </Button>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle>Upload Recipients</CardTitle>
          <CardDescription>
            Send mails by uploading recipients in CSV. (format: [FirstName, LastName, Email, PhoneNumber (with country code without +), Address] in exact sequence)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-12 text-center hover:border-muted-foreground/50 transition-colors cursor-pointer"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              onChange={handleFileUpload}
              className="hidden"
            />
            
            {isProcessing ? (
              <div className="space-y-4">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                <p>Processing file...</p>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                <div>
                  <p className="font-medium">Pick or Drop File here to upload it</p>
                  <p className="text-sm text-muted-foreground mt-1">text/csv, application/vnd.ms-excel</p>
                </div>
              </div>
            )}
            
            {uploadedFile && (
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span className="text-sm font-medium">{uploadedFile.name}</span>
                  <Badge variant="secondary">{(uploadedFile.size / 1024).toFixed(1)} KB</Badge>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Validation Results */}
      {validationResult && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Rows</p>
                    <p className="text-2xl font-bold">{validationResult.totalRows}</p>
                  </div>
                  <FileText className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Valid Rows</p>
                    <p className="text-2xl font-bold text-green-600">{validationResult.validRows}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Invalid Rows</p>
                    <p className="text-2xl font-bold text-red-600">{validationResult.invalidRows}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Validation Alerts */}
          {validationResult.invalidRows > 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {validationResult.invalidRows} rows have validation errors and will be skipped during sending.
              </AlertDescription>
            </Alert>
          )}

          {/* Data Preview */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Data Preview</CardTitle>
                  <CardDescription>
                    Review your data before sending
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setPreviewDialog({ isOpen: true, type: "email" })}
                    disabled={validationResult.validRows === 0}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview Emails
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setPreviewDialog({ isOpen: true, type: "sms" })}
                    disabled={validationResult.validRows === 0}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview SMS
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="valid">
                <TabsList>
                  <TabsTrigger value="valid">
                    Valid ({validationResult.validRows})
                  </TabsTrigger>
                  <TabsTrigger value="invalid">
                    Invalid ({validationResult.invalidRows})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="valid" className="mt-4">
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Message</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {validationResult.valid.slice(0, 10).map((recipient, index) => (
                          <TableRow key={index}>
                            <TableCell>{recipient.firstName} {recipient.lastName}</TableCell>
                            <TableCell>{recipient.email}</TableCell>
                            <TableCell>{recipient.phoneNumber}</TableCell>
                            <TableCell className="max-w-xs truncate">{recipient.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {validationResult.valid.length > 10 && (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        Showing first 10 of {validationResult.valid.length} valid recipients
                      </div>
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="invalid" className="mt-4">
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Errors</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {validationResult.invalid.slice(0, 10).map((recipient, index) => (
                          <TableRow key={index}>
                            <TableCell>{recipient.firstName} {recipient.lastName}</TableCell>
                            <TableCell>{recipient.email}</TableCell>
                            <TableCell>{recipient.phoneNumber}</TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {recipient.errors.map((error, i) => (
                                  <Badge key={i} variant="destructive" className="text-xs">
                                    {error}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {validationResult.invalid.length > 10 && (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        Showing first 10 of {validationResult.invalid.length} invalid recipients
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Send Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Send Messages</CardTitle>
              <CardDescription>
                Send emails or SMS to {validationResult.validRows} valid recipients
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isSending ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span>Sending messages...</span>
                    <span>{Math.round(sendProgress)}%</span>
                  </div>
                  <Progress value={sendProgress} className="w-full" />
                </div>
              ) : sendComplete ? (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Messages sent successfully to {validationResult.validRows} recipients!
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="flex gap-4">
                  <Button
                    onClick={() => handleSend("email")}
                    disabled={validationResult.validRows === 0}
                    className="flex-1"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Send Emails ({validationResult.validRows})
                  </Button>
                  <Button
                    onClick={() => handleSend("sms")}
                    disabled={validationResult.validRows === 0}
                    variant="outline"
                    className="flex-1"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Send SMS ({validationResult.validRows})
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewDialog.isOpen} onOpenChange={(open) => setPreviewDialog(prev => ({ ...prev, isOpen: open }))}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewDialog.type === "email" ? (
                <Mail className="h-4 w-4" />
              ) : (
                <Phone className="h-4 w-4" />
              )}
              {previewDialog.type === "email" ? "Email" : "SMS"} Preview
            </DialogTitle>
            <DialogDescription>
              Preview of messages that will be sent to recipients
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {validationResult?.valid.slice(0, 5).map((recipient, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium">
                    {recipient.firstName} {recipient.lastName}
                  </div>
                  <Badge variant="outline">
                    {previewDialog.type === "email" ? recipient.email : recipient.phoneNumber}
                  </Badge>
                </div>
                <div className="text-sm bg-muted p-3 rounded">
                  {recipient.message}
                </div>
              </div>
            ))}
            {validationResult && validationResult.valid.length > 5 && (
              <div className="text-center text-sm text-muted-foreground">
                Showing preview for first 5 recipients. All {validationResult.valid.length} will receive similar messages.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}